import { useEffect, useState, useContext } from "react";
import { Helmet } from "react-helmet-async";
import apiWithRefresh from "../utils/apiWithRefresh";
import RoleEnum from "../utils/RoleEnum";
import profileTranslations from "../locales/profileTranslations";
import { LanguageContext } from "../locales/LanguageContext";
import {
  Card,
  Avatar,
  Grid,
  Typography,
  Chip,
  Box,
  Tooltip,
  Divider,
  Button,
  LinearProgress,
  Badge,
} from "@mui/material";
import {
  Email,
  Phone,
  School,
  Schedule,
  BatchPrediction,
  CalendarMonth,
  Lock,
  Person,
  LabelImportant,
  EmojiEvents,
} from "@mui/icons-material";

const Profile = () => {
  const { lang } = useContext(LanguageContext);
  const t = profileTranslations[lang] || {};
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [levelCompletions, setLevelCompletions] = useState([]);

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiWithRefresh.get("auth/me");
        setUser(data);
        // If learner, fetch enrollment details
        if (data.roleId === RoleEnum.LEARNER && data.enrollmentId) {
          const enrollmentDetail = await apiWithRefresh.get(
            `learner-enrollments/${data.enrollmentId}`,
          );
          setEnrollment(enrollmentDetail);
          // call api : level-completions/{learnerId}/learner to get completions
          const completions = await apiWithRefresh.get(
            `level-completions/${data.id}/learner`,
          );
          setLevelCompletions(Array.isArray(completions) ? completions : []);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  // const theme = useTheme();
  // Calculate progress for learner (levels completed / total levels if available)
  let progress = null;
  let totalLevels = null;
  if (
    user &&
    user.roleId === RoleEnum.LEARNER &&
    enrollment &&
    enrollment.preferredLevel?.totalLevels
  ) {
    totalLevels = enrollment.preferredLevel.totalLevels;
    progress =
      levelCompletions.length && totalLevels
        ? Math.round((levelCompletions.length / totalLevels) * 100)
        : 0;
  }

  // Motivational quotes
  const quotes = [
    "Learning is a treasure that will follow its owner everywhere.",
    "Success is the sum of small efforts, repeated day in and day out.",
    "The beautiful thing about learning is nobody can take it away from you.",
    "Every accomplishment starts with the decision to try.",
    "Education is the passport to the future.",
  ];
  const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];

  // Confetti effect for completed levels (simple emoji burst)
  const showConfetti =
    user && user.roleId === RoleEnum.LEARNER && levelCompletions.length > 0;

  return (
    <>
      <Helmet>
        <title>{t.pageTitle} | AKAAL KIDS</title>
      </Helmet>
      {/* Animated background */}
      <Box
        sx={{
          width: "100%",
          minHeight: 220,
          background:
            "linear-gradient(270deg, #f6815e, #f6b93b, #fffbe6, #f6815e)",
          backgroundSize: "600% 600%",
          color: "#fff",
          py: 5,
          textAlign: "center",
          mb: 4,
          boxShadow: "0 4px 24px #f6815e22",
          position: "relative",
          overflow: "hidden",
          animation: "gradientBG 12s ease infinite",
          "@keyframes gradientBG": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
        }}
      >
        <Typography
          variant="h2"
          fontWeight={900}
          sx={{ letterSpacing: 1, mb: 1, textShadow: "0 2px 12px #f6815e55" }}
        >
          {t.heroTitle}
        </Typography>
        <Typography variant="h5" fontWeight={500} sx={{ opacity: 0.96 }}>
          <Person sx={{ verticalAlign: "middle", mr: 1 }} />
          {t.heroDesc}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{ mt: 2, fontStyle: "italic", opacity: 0.9 }}
        >
          {randomQuote}
        </Typography>
        {showConfetti && (
          <Box
            sx={{
              fontSize: 32,
              mt: 2,
              letterSpacing: 2,
              animation: "pop 1.2s",
            }}
          >
            🎉✨🎊👏
          </Box>
        )}
      </Box>
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={10} md={7} lg={5}>
          <Box sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
            {loading && <Typography>{t.loading}</Typography>}
            {error && (
              <Typography color="error" mb={3}>
                {t.error}: {error}
              </Typography>
            )}
            {!loading && !error && user && (
              <Card
                sx={{
                  p: 4,
                  borderRadius: 4,
                  boxShadow: 12,
                  background:
                    "linear-gradient(120deg, #fffbe6 60%, #f6b93b22 100%)",
                  transition: "transform 0.25s cubic-bezier(.4,2,.6,1)",
                  "&:hover": {
                    transform: "scale(1.025)",
                    boxShadow: 20,
                  },
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  mb={3}
                >
                  <Badge
                    badgeContent={user.roleName}
                    color="secondary"
                    sx={{
                      "& .MuiBadge-badge": {
                        right: -18,
                        top: 18,
                        fontWeight: 700,
                        fontSize: 13,
                        background: "#f6815e",
                        color: "#fff",
                        px: 2,
                        borderRadius: 2,
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 90,
                        height: 90,
                        bgcolor: "#f6815e",
                        fontSize: 36,
                        mb: 1,
                        boxShadow: 3,
                      }}
                    >
                      {user.name ? (
                        user.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()
                          .slice(0, 2)
                      ) : (
                        <Person fontSize="large" />
                      )}
                    </Avatar>
                  </Badge>
                  <Typography variant="h5" fontWeight={700}>
                    {user.name}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box mb={2}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Email sx={{ mr: 1, color: "#f6815e" }} />
                    <Typography variant="body1">{user.email}</Typography>
                  </Box>
                  {user.level && (
                    <Box display="flex" alignItems="center" mb={1}>
                      <School sx={{ mr: 1, color: "#f6b93b" }} />
                      <Typography variant="body1">
                        {t.level}: {user.level}
                      </Typography>
                    </Box>
                  )}
                  {user.enrolledSince && (
                    <Box display="flex" alignItems="center" mb={1}>
                      <CalendarMonth sx={{ mr: 1, color: "#f6b93b" }} />
                      <Typography variant="body1">
                        {t.enrolledSince}: {user.enrolledSince}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {/* Enrollment details for learner role */}
                {user.roleId === RoleEnum.LEARNER && enrollment && (
                  <Box mb={2}>
                    <Typography variant="subtitle1" fontWeight={600} mb={1}>
                      {t.enrollmentDetails}
                    </Typography>
                    <Box ml={1} mb={1}>
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <Phone sx={{ mr: 1, color: "#f6815e" }} />
                        <Typography variant="body2">
                          {enrollment.phone || t.notSpecified}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <BatchPrediction sx={{ mr: 1, color: "#f6b93b" }} />
                        <Typography variant="body2">
                          {t.batchAllocated}:{" "}
                          {enrollment.batch?.name || t.notAllocated}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <Schedule sx={{ mr: 1, color: "#f6b93b" }} />
                        <Typography variant="body2"> 
                          {enrollment.batch?.startDate
                            ? new Date(
                                enrollment.batch.startDate,
                              ).toDateString()
                            : t.notSpecified}{" - "}
                          {enrollment.batch?.endDate
                            ? new Date(
                                enrollment.batch.endDate,
                              ).toDateString()
                            : t.notSpecified}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mb={0.5}>
                         <School sx={{ mr: 1, color: "#f6b93b" }} />
                        <Typography variant="body2">
                          {t.feePlan}:{" "}
                          {enrollment.feePlan?.name || t.notSpecified}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Completed Levels List */}
                    {levelCompletions.length > 0 ? (
                      <Box ml={1} mt={2}>
                        <Typography variant="subtitle2" fontWeight={500} mb={1}>
                          <EmojiEvents
                            sx={{
                              color: "#f6b93b",
                              mr: 1,
                              verticalAlign: "middle",
                            }}
                          />
                          {t.completedLevels}:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {levelCompletions.map((comp) => (
                            <Tooltip
                              key={comp.id}
                              title={
                                comp.completedAt
                                  ? new Date(
                                      comp.completedAt,
                                    ).toLocaleDateString()
                                  : "-"
                              }
                            >
                              <Chip
                                icon={<School sx={{ color: "#fff" }} />}
                                label={comp.level?.name || t.level}
                                color="success"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: 15,
                                  px: 1.5,
                                  background:
                                    "linear-gradient(90deg,#f6b93b 60%,#f6815e 100%)",
                                  color: "#fff",
                                  boxShadow: 2,
                                }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                        {progress !== null && totalLevels && (
                          <Box mt={2}>
                            <Typography
                              variant="body2"
                              mb={0.5}
                              color="text.secondary"
                            >
                              Progress: {levelCompletions.length} /{" "}
                              {totalLevels} levels completed
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{
                                height: 10,
                                borderRadius: 5,
                                background: "#ffe0b2",
                                "& .MuiLinearProgress-bar": {
                                  background:
                                    "linear-gradient(90deg,#f6815e,#f6b93b)",
                                },
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box ml={1} mt={2}>                       
                        <Typography variant="body2" display="inline">
                           <LabelImportant sx={{ mr: 1, color: "#f6815e" }} />
                          {enrollment.preferredLevel?.name || t.notSpecified}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="center">
                  <Button
                    href="/change-password"
                    variant="outlined"
                    startIcon={<Lock />}
                    sx={{
                      borderColor: "#f6815e",
                      color: "#f6815e",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "#f6b93b",
                        color: "#f6b93b",
                        background: "#fffbe6",
                      },
                    }}
                  >
                    {t.changePassword}
                  </Button>
                </Box>
              </Card>
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default Profile;
